import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { doLogin } from '../../redux/actions';
import ProdigyLogo from '../../../ui/icons/ProdigyLogo';
import ProdigyLogoFull from '../../../ui/icons/ProdigyLogoFull';
import ErrorBox from '../../components/ErrorBox';
import ForgetPassword from '../../components/ForgetPassword';
import InputLogin from '../../components/InputLogin';
import ButtonLogin from '../../components/ButtonLogin';
import MainTerms from '../../components/MainTerms';
import ExternalLink from '../../components/ExternalLink';
import SignUp from '../../components/SignUp';
import { errorsSelector, submittedSelector } from '../../redux/selectors';
import { Button, ButtonInverted } from '../../../../styled-components';
import { redirectTo } from '../../../../common/modules/Navigation/Navigation.helpers';
import StoreButtons from '../../components/StoreButtons';
import LoginWebToMobileQRCode from '../../../../assets/logos/login-web-to-mobile-qr';

const Container = styled.div`
  padding: 40px 40px 0 40px;

  @media screen and (min-width: ${(props) => props.theme.widths.max768}) {
    padding: 60px 60px 0 60px;
  }

  @media screen and (min-width: ${(props) => props.theme.widths.max1024}) {
    padding: 130px 130px 0 130px;
  }

  @media screen and (min-width: ${(props) => props.theme.widths.max2048}) {
    padding: 130px 130px 0 400px;
  }

  @media screen and (min-width: ${(props) => props.theme.widths.max2560}) {
    padding: 130px 130px 0 800px;
  }
`;

const Welcome = styled.h2`
  font-size: 60px;
  font-weight: 300;
  margin: 0 auto;
  color: white;

  @media screen and (max-width: ${(props) => props.theme.widths.max768}) {
    font-size: 32px;
    text-align: center;
  }
`;

const Logo = styled.h1`
  margin: 0 auto;

  span {
    width: 100%;
  }

  @media screen and (max-width: ${(props) => props.theme.widths.max768}) {
    margin: 30px auto;
    text-align: center;
  }

  svg {
    width: 500px;

    @media screen and (max-width: ${(props) => props.theme.widths.max768}) {
      max-width: 240px;
      width: auto;
    }
  }
`;

const Subtitle = styled.p`
  margin: 50px 0;
  font-size: 40px;
  font-weight: 300;
  color: white;
  max-width: 700px;

  @media screen and (max-width: ${(props) => props.theme.widths.max768}) {
    font-size: 24px;
    font-weight: normal;
    margin: 30px 0;
  }
`;

const buttonStyle = css`
  max-width: 240px;
  display: inline-block;
  border-radius: 28px;
  margin: 0 auto;

  @media screen and (max-width: ${(props) => props.theme.widths.max768}) {
    display: block;
  }
`;

const SignInButton = styled(Button)`
  ${buttonStyle}
`;

const SignUpButton = styled(ButtonInverted)`
  ${buttonStyle};
  margin-left: 30px;
  background-color: inherit;

  @media screen and (max-width: ${(props) => props.theme.widths.max768}) {
    margin: 30px auto;
  }
`;

const MainTermsContainer = styled.p`
  margin: 80px auto 30px;
  text-align: center;
  bottom: -1px;

  @media screen and (max-width: ${(props) => props.theme.widths.max768}) {
    margin: 46px auto 30px;
  }

  a {
    font-size: 16px;
    font-weight: 600;
    color: #00b1eb;
    text-decoration: none;
  }
`;

const Divider = styled.span`
  color: white;
  font-size: 16px;
  font-weight: 600;
`;

class LoginForm extends Component {
  constructor(props) {
    super(props);
    const { fromPath } = props;
    this.state = {
      email: '',
      password: '',
      fromPath,
      signingin: false,
    };
  }

  onSignInButtonClick = (e) => {
    e.preventDefault();
    this.setState({ signingin: true });
    redirectTo(`${process.env.REACT_APP_AUTH_BASE_URL}/customers/sign-in?returnTo=${window.location.href}`);
  };

  onCreateAccountClick = (e) => {
    e.preventDefault();
    redirectTo(`${process.env.REACT_APP_AUTH_BASE_URL}/customers/create-account?returnTo=${window.location.href}`);
  };

  handleSubmit = (e) => {
    const { onLogin } = this.props;
    e.preventDefault();
    const { email, password, fromPath } = this.state;
    onLogin(email, password, fromPath);
  };

  handleChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  };

  emailInput = (submitted) => {
    const { email } = this.state;
    const disabled = submitted ? 'disabled' : undefined;
    return (
      <InputLogin
        type="email"
        name="email"
        label="Email Address"
        placeholder="Email"
        value={email}
        disabled={disabled}
        onChange={this.handleChange}
      />
    );
  };

  passwordInput = (submitted) => {
    const { password } = this.state;
    const disabled = submitted ? 'disabled' : undefined;
    return (
      <InputLogin
        type="password"
        name="password"
        label="Password"
        placeholder="Password"
        value={password}
        disabled={disabled}
        onChange={this.handleChange}
      />
    );
  };

  renderLoginForm = () => {
    const { errors, submitted } = this.props;
    const forgetPasswordUrl = `${process.env.REACT_APP_ACCOUNTS_URL}${process.env.REACT_APP_ENDPOINT_ACCOUNT_RESET_PASSWORD}`;
    const buttonText = submitted ? 'Signing in...' : 'Sign in';

    return (
      <div className="main">
        <form onSubmit={this.handleSubmit} className="main__form" id="login">
          <h1 className="main__logo">
            <ProdigyLogo color="#00B1EB" />
          </h1>
          <h2>Welcome to Prodigy Finance</h2>
          <ErrorBox errors={errors} />
          {this.emailInput(submitted)}
          {this.passwordInput(submitted)}
          <ForgetPassword url={forgetPasswordUrl} />
          <ButtonLogin submitted={submitted} buttonText={buttonText} />
          <SignUp />
          <MainTerms />
        </form>
      </div>
    );
  };

  renderOauthFlow = () => {
    const { signingin } = this.state;

    return (
      <div className="main-sign-in">
        <Container>
          <Welcome>Welcome to</Welcome>
          <Logo>
            <ProdigyLogoFull color="#00B1EB" />
          </Logo>
          <Subtitle>Graduate student loans for a global education</Subtitle>
          <SignInButton id="signin-button" disabled={signingin} onClick={this.onSignInButtonClick}>
            SIGN IN
          </SignInButton>
          <SignUpButton id="create-account-button" disabled={signingin} onClick={this.onCreateAccountClick}>
            CREATE ACCOUNT
          </SignUpButton>

          <StoreButtons>
            <LoginWebToMobileQRCode />
          </StoreButtons>

          <MainTermsContainer>
            <ExternalLink url="https://prodigy-files.s3.amazonaws.com/files/bVTza_GuG3Zb_1i2inmmyg/Prodigy_Finance_Ltd_EULA.v2.03_January_2023.pdf">
              EULA
            </ExternalLink>
            <Divider>|</Divider>
            <ExternalLink url="https://prodigyfinance.com/legal/terms-of-use">Terms of Service</ExternalLink>
            <Divider>|</Divider>
            <ExternalLink url="https://prodigy-files.s3.amazonaws.com/files/gHXqF7JvpTVTfzPZFwvWIQ/Prodigy_Finance_Privacy_Notice_-_January_2023.pdf">
              Privacy Notice
            </ExternalLink>
            <Divider>|</Divider>
            <ExternalLink url="https://prodigyfinance.com/legal/privacy-policy">Privacy Policy</ExternalLink>
          </MainTermsContainer>
        </Container>
      </div>
    );
  };

  render() {
    const { oAuthFlowEnabled } = this.props;

    return oAuthFlowEnabled ? this.renderOauthFlow() : this.renderLoginForm();
  }
}

LoginForm.propTypes = {
  onLogin: PropTypes.func.isRequired,
  fromPath: PropTypes.string.isRequired,
  submitted: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.object),
};

LoginForm.defaultProps = {
  errors: [],
  submitted: false,
};

export const mapStateToProps = (state) => ({
  errors: errorsSelector(state),
  submitted: submittedSelector(state),
  oAuthFlowEnabled: process.env.REACT_APP_OAUTH_FLOW === 'true',
});

export const mapDispatchToProps = (dispatch) => ({
  onLogin: (email, password, fromPath) => dispatch(doLogin(email, password, fromPath)),
});

export default LoginForm;
